import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  UserPlus,
  MoreHorizontal,
  Loader2,
  Users,
  KeyRound,
  Activity,
  Monitor,
  Copy,
  RefreshCw,
  Trash2,
  CheckCircle2,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useLocations } from '@/hooks/useLocations';
import { useAuth } from '@/hooks/useAuth';
import { useKiosk, type KioskDevice, type CreateKioskInput } from '@/hooks/useKiosk';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { TeamManagement } from '@/components/team/TeamManagement';

type Tab = 'users' | 'kiosks' | 'roles' | 'activity';

// Relative time formatter
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
  if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
  if (diffYears === 1) return 'last year';
  return `${diffYears} years ago`;
}

export default function Team() {
  const navigate = useNavigate();
  const { locations } = useLocations();
  const { profile, isSuperadmin, isAdmin } = useAuth();
  const {
    kioskDevices,
    isLoading: kioskLoading,
    createKiosk,
    updateKiosk,
    deleteKiosk,
    regenerateCode,
    refetch: refetchKiosks,
  } = useKiosk();

  const [activeTab, setActiveTab] = useState<Tab>('users');

  // Kiosk state
  const [createKioskDialogOpen, setCreateKioskDialogOpen] = useState(false);
  const [kioskFormData, setKioskFormData] = useState<CreateKioskInput>({
    name: '',
    location_id: '',
  });
  const [kioskFormError, setKioskFormError] = useState<string | null>(null);
  const [deletingKiosk, setDeletingKiosk] = useState<string | null>(null);
  const [regeneratingCode, setRegeneratingCode] = useState<string | null>(null);

  const canManageStaff = isSuperadmin || isAdmin || profile?.role === 'owner';

  const tabs = [
    { id: 'users' as const, label: 'Users', icon: Users },
    { id: 'kiosks' as const, label: 'Kiosks', icon: Monitor },
    { id: 'roles' as const, label: 'Roles', icon: KeyRound },
    { id: 'activity' as const, label: 'Activity', icon: Activity },
  ];

  // Kiosk handlers
  const handleCreateKiosk = async (e: React.FormEvent) => {
    e.preventDefault();
    setKioskFormError(null);

    if (!kioskFormData.name || !kioskFormData.location_id) {
      setKioskFormError('Please fill in all required fields');
      return;
    }

    try {
      await createKiosk.mutateAsync(kioskFormData);
      setCreateKioskDialogOpen(false);
      setKioskFormData({ name: '', location_id: '' });
      toast.success('Kiosk created', {
        description: 'New kiosk device has been added',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create kiosk';
      setKioskFormError(message);
    }
  };

  const handleDeleteKiosk = async (id: string) => {
    if (!confirm('Are you sure you want to delete this kiosk device?')) return;

    setDeletingKiosk(id);
    try {
      await deleteKiosk.mutateAsync(id);
      toast.success('Kiosk deleted', {
        description: 'Kiosk device has been removed',
      });
    } catch {
      toast.error('Error', {
        description: 'Failed to delete kiosk',
      });
    } finally {
      setDeletingKiosk(null);
    }
  };

  const handleRegenerateCode = async (id: string) => {
    setRegeneratingCode(id);
    try {
      const newCode = await regenerateCode.mutateAsync(id);
      toast.success('Code regenerated', {
        description: `New code: ${newCode}`,
      });
      refetchKiosks();
    } catch {
      toast.error('Error', {
        description: 'Failed to regenerate code',
      });
    } finally {
      setRegeneratingCode(null);
    }
  };

  const handleToggleKioskActive = async (kiosk: KioskDevice, isActive: boolean) => {
    try {
      await updateKiosk.mutateAsync({ id: kiosk.id, updates: { is_active: isActive } });
      toast.success(isActive ? 'Kiosk enabled' : 'Kiosk disabled', {
        description: `${kiosk.name} is now ${isActive ? 'active' : 'inactive'}`,
      });
    } catch {
      toast.error('Error', {
        description: 'Failed to update kiosk status',
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast('Copied!', {
      description: 'Device code copied to clipboard',
      duration: 2000,
    });
  };

  const formatRelativeKioskTime = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return formatRelativeTime(dateString);
  };

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-display font-semibold tracking-tight">
          Users
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage users and their sales targets
        </p>
      </div>
      {canManageStaff && activeTab === 'users' && (
        <Button variant="blaze" className="gap-2" onClick={() => navigate('/command-center/users')}>
          <UserPlus className="h-4 w-4" />
          Add user
        </Button>
      )}
    </div>
  );

  return (
    <DashboardLayout header={header}>
      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'users' && (
        <TeamManagement showHeader={false} />
      )}

      {activeTab === 'kiosks' && (
        <>
          {/* Kiosk Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm text-muted-foreground">
                Set up self-service kiosk stations for customer ordering
              </p>
            </div>
            {canManageStaff && (
              <Button variant="blaze" className="gap-2" onClick={() => setCreateKioskDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Add Kiosk
              </Button>
            )}
          </div>

          {/* Kiosks Grid */}
          {kioskLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading kiosks...</span>
              </div>
            </div>
          ) : kioskDevices.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12 text-muted-foreground">
                  <Monitor className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="font-medium text-foreground mb-2">No kiosks configured</h3>
                  <p className="text-sm mb-4">
                    Add a kiosk device to enable self-service ordering
                  </p>
                  {canManageStaff && (
                    <Button variant="blaze" className="gap-2" onClick={() => setCreateKioskDialogOpen(true)}>
                      <Plus className="h-4 w-4" />
                      Add your first kiosk
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {kioskDevices.map((kiosk) => (
                <Card key={kiosk.id} className={cn(!kiosk.is_active && 'opacity-60')}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'w-10 h-10 rounded-lg flex items-center justify-center',
                          kiosk.is_active ? 'bg-cyan-500/10' : 'bg-gray-500/10'
                        )}>
                          <Monitor className={cn(
                            'h-5 w-5',
                            kiosk.is_active ? 'text-cyan-600' : 'text-gray-500'
                          )} />
                        </div>
                        <div>
                          <h3 className="font-semibold">{kiosk.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {kiosk.location?.name || 'Unknown location'}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleRegenerateCode(kiosk.id)}
                            disabled={regeneratingCode === kiosk.id}
                          >
                            {regeneratingCode === kiosk.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            Regenerate Code
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => window.open('/kiosk/setup', '_blank')}
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open Setup Page
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteKiosk(kiosk.id)}
                            disabled={deletingKiosk === kiosk.id}
                            className="text-red-600"
                          >
                            {deletingKiosk === kiosk.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            Delete Kiosk
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Device Code */}
                    <div className="bg-muted rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">Device Code</span>
                        <button
                          onClick={() => copyToClipboard(kiosk.device_code)}
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                        >
                          <Copy className="h-3 w-3" />
                          Copy
                        </button>
                      </div>
                      <p className="font-mono text-lg tracking-widest font-bold">
                        {kiosk.device_code}
                      </p>
                    </div>

                    {/* Status */}
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-muted-foreground">Status</span>
                      <div className="flex items-center gap-2">
                        {kiosk.device_token ? (
                          <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Registered
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                            <XCircle className="h-3 w-3 mr-1" />
                            Pending Setup
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Last Active */}
                    <div className="flex items-center justify-between text-sm mb-4">
                      <span className="text-muted-foreground">Last Active</span>
                      <span>{formatRelativeKioskTime(kiosk.last_active_at)}</span>
                    </div>

                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      <span className="text-sm font-medium">Enabled</span>
                      <Switch
                        checked={kiosk.is_active}
                        onCheckedChange={(checked) => handleToggleKioskActive(kiosk, checked)}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* How to Setup Info */}
          <Card className="mt-6">
            <CardContent className="p-5">
              <h4 className="font-medium mb-3">How to set up a kiosk</h4>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Create a new kiosk device above with a name and location</li>
                <li>Note the 6-character device code shown on the kiosk card</li>
                <li>On the kiosk device (tablet/touchscreen), navigate to <code className="bg-muted px-1 rounded">/kiosk/setup</code></li>
                <li>Enter the device code to register the kiosk</li>
                <li>The kiosk is now ready for customer self-ordering!</li>
              </ol>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'roles' && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12 text-muted-foreground">
              <KeyRound className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h3 className="font-medium text-foreground mb-2">Role Management</h3>
              <p className="text-sm">
                View and manage role-based permissions and access levels for your team.
              </p>
              <p className="text-sm mt-2 mb-4">
                Full role and permission management is available in the Command Center.
              </p>
              <Button
                variant="blaze"
                className="gap-2"
                onClick={() => navigate('/command-center/roles')}
              >
                <ExternalLink className="h-4 w-4" />
                Open Role Management
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'activity' && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h3 className="font-medium text-foreground mb-2">Activity Log</h3>
              <p className="text-sm">
                View recent user activity, logins, and actions taken in the system.
              </p>
              <p className="text-sm mt-1">Coming soon.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Kiosk Dialog */}
      <ResponsiveModal
        open={createKioskDialogOpen}
        onOpenChange={(value) => {
          if (!value) {
            setKioskFormData({ name: '', location_id: '' });
            setKioskFormError(null);
          }
          setCreateKioskDialogOpen(value);
        }}
      >
        <ResponsiveModalContent maxWidth="md">
          <form onSubmit={handleCreateKiosk} className="flex flex-col h-full">
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>Add New Kiosk</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Create a new self-service kiosk station for customer ordering.
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <ResponsiveModalBody>
              <div className="grid gap-4">
                {kioskFormError && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg">
                    {kioskFormError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="kiosk_name">Kiosk Name *</Label>
                  <Input
                    id="kiosk_name"
                    value={kioskFormData.name}
                    onChange={(e) => setKioskFormData({ ...kioskFormData, name: e.target.value })}
                    placeholder="e.g., Front Counter Kiosk"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="kiosk_location">Location *</Label>
                  <Select
                    value={kioskFormData.location_id}
                    onValueChange={(value) => setKioskFormData({ ...kioskFormData, location_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    The kiosk will show products and create orders for this location
                  </p>
                </div>
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateKioskDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="blaze"
                disabled={createKiosk.isPending}
              >
                {createKiosk.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Kiosk
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </DashboardLayout>
  );
}
